import { useCallback, useEffect, useState } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Alert, Badge, Box, Tab, Typography, Paper } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import "./workspace.css";
import { openGlobalSnack } from "../utils/snack";
import { HS_AGENT, hsRequest } from "./api";
import { LeadChat } from "./leadchat";
import { LeadList } from "./leadlist";

/** Área do atendente: fila de leads, meus leads (com conversa), histórico. */
export function HumanSalesAgent() {
    const [tab, setTab] = useState('mine');
    const [meta, setMeta] = useState<any>(null);
    const [stats, setStats] = useState<any>(null);
    const [open, setOpen] = useState<any>(null);
    const [version, setVersion] = useState(0);

    const refresh = useCallback(async () => {
        try {
            const [m, s] = await Promise.all([hsRequest('GET', `${HS_AGENT}/meta`), hsRequest('GET', `${HS_AGENT}/stats`)]);
            setMeta(m); setStats(s);
        } catch (e: any) { openGlobalSnack(e.message, 'error'); }
    }, []);
    useEffect(() => { refresh(); }, [refresh, version]);

    useEffect(() => { const timer = setInterval(refresh, 20000); return () => clearInterval(timer); }, [refresh]);
    function changed() {
        setVersion((v) => v + 1);
        if (open) hsRequest('GET', `${HS_AGENT}/leads/${open.id}`).then((r) => setOpen((current: any) => current?.id === r.lead.id ? r.lead : current)).catch(() => { /* closed or transferred: dialog closes via its action */ });
    }

    return (
        <Box className="sales-workspace">
            <Box sx={{ mb: 3 }}><Typography variant="overline" color="text.secondary">ÁREA DO COLABORADOR</Typography><Typography component="h1" variant="h4" sx={{ fontWeight: 700, fontSize: { xs: 26, md: 32 } }}>Atendimento</Typography><Typography color="text.secondary" sx={{ mt: 0.5 }}>Organize sua fila e acompanhe cada cliente até a conclusão.</Typography></Box>
            {meta && !meta.enabled ? <Alert severity="warning" sx={{ mb: 2 }}>O programa de atendimento está desativado no momento.</Alert> : null}
            {stats && (stats.replies || stats.firstContact) ? <Alert severity={stats.replies ? 'warning' : 'info'} sx={{ mb: 2 }}>
                {[stats.replies ? `${stats.replies} aguardando sua resposta` : '', stats.firstContact ? `${stats.firstContact} para primeiro contato` : ''].filter(Boolean).join(' • ')}
            </Alert> : null}
            {stats ? <Accordion disableGutters elevation={0} sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: '12px !important', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="body2">Meu desempenho <Typography component="span" variant="body2" color="text.secondary">• {stats.won} vendas concluídas</Typography></Typography></AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                        {[
                            ['Em atendimento', `${stats.active} / ${meta?.maxActive ?? '—'}`],
                            ['Vendas concluídas', String(stats.won)],
                            ['Conversão', `${stats.conversionPercent}%`],
                            ['Carteira', `${stats.portfolio ?? stats.active} / ${meta?.maxPortfolio ?? '—'}`],
                        ].map(([label, value]) => <Paper key={label} variant="outlined" sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{label}</Typography><Typography variant="h6">{value}</Typography></Paper>)}
                    </Box>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>Conversão: vendas pagas sobre vendas pagas e perdas. Transferidos ao robô: {stats.transferred ?? 0} • Encerrados sem venda: {stats.lost}.</Typography>
                    {stats.recent ? <Typography variant="caption" display="block" color="text.secondary">Últimos 30 dias: {stats.recent.assigned} clientes assumidos • {stats.recent.won} pagos • Primeiro contato: {stats.recent.medianFirstContactMin == null ? 'sem dados' : `${stats.recent.medianFirstContactMin} min (mediana)`}.</Typography> : null}
                </AccordionDetails>
            </Accordion> : null}
            <TabContext value={tab}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList aria-label="Listas de atendimento" variant="fullWidth" onChange={(_, v) => setTab(v)}>
                        <Tab label="Fila" value="queue" />
                        <Tab value="mine" label={<Badge color="primary" badgeContent={stats?.portfolio || 0} sx={{ pr: stats?.portfolio ? 1.5 : 0 }}>Meus clientes</Badge>} />
                        <Tab label="Histórico" value="history" />
                    </TabList>
                </Box>
                <TabPanel value="queue" sx={{ px: 0 }}><LeadList key={`q${version}`} scope="queue" meta={meta} onOpen={setOpen} onClaimed={() => { changed(); setTab('mine'); }} /></TabPanel>
                <TabPanel value="mine" sx={{ px: 0 }}><LeadList key={`m${version}`} scope="mine" meta={meta} onOpen={setOpen} onClaimed={changed} /></TabPanel>
                <TabPanel value="history" sx={{ px: 0 }}><LeadList key={`h${version}`} scope="history" meta={meta} onOpen={setOpen} onClaimed={changed} /></TabPanel>
            </TabContext>
            {open ? <LeadChat lead={open} onClose={() => setOpen(null)} onChanged={changed} /> : null}
        </Box>
    );
}
